import type { Substance, SubstanceClass } from '../types';
import { RECEPTOR_IDS, RECEPTOR_TO_REGIONS } from '../receptors';
import { REGION_IDS } from '../regions';
import { STIMULANTS } from './stimulants';
import { DEPRESSANTS } from './depressants';
import { OPIOIDS } from './opioids';
import { PSYCHEDELICS } from './psychedelics';
import { CANNABINOIDS } from './cannabinoids';
import { MEDICINES } from './medicines';
import { ENDOGENOUS } from './endogenous';

/**
 * Substances are grouped into one module per class rather than one file per
 * substance. With this many entries, per-class files are far easier to navigate,
 * and adding a substance is still a single-file change plus one line here.
 */
export const SUBSTANCE_LIST: Substance[] = [
  ...STIMULANTS,
  ...DEPRESSANTS,
  ...OPIOIDS,
  ...PSYCHEDELICS,
  ...CANNABINOIDS,
  ...MEDICINES,
  ...ENDOGENOUS,
];

export const SUBSTANCES: Record<string, Substance> = Object.fromEntries(
  SUBSTANCE_LIST.map((substance) => [substance.id, substance]),
);

/** Substances grouped by class, preserving declaration order within each group. */
export function substancesByClass(): Map<SubstanceClass, Substance[]> {
  const grouped = new Map<SubstanceClass, Substance[]>();
  for (const substance of SUBSTANCE_LIST) {
    const bucket = grouped.get(substance.substanceClass) ?? [];
    bucket.push(substance);
    grouped.set(substance.substanceClass, bucket);
  }
  return grouped;
}

/**
 * Validates every substance against the data contract's invariants, throwing on
 * the first violation with a message specific enough to fix without spelunking.
 * Run from tests/substanceRegistry.test.ts so a malformed entry fails CI rather
 * than silently rendering a dead region node.
 */
export function validateSubstanceRegistry(): void {
  const seenIds = new Set<string>();
  const seenAliases = new Map<string, string>();

  for (const substance of SUBSTANCE_LIST) {
    const prefix = `Substance "${substance.id}"`;

    if (!substance.id) throw new Error('A substance is missing an id');
    if (seenIds.has(substance.id)) throw new Error(`${prefix}: duplicate id`);
    seenIds.add(substance.id);

    if (!substance.displayName) throw new Error(`${prefix}: missing displayName`);
    if (!substance.mechanismSummary) throw new Error(`${prefix}: missing mechanismSummary`);
    if (!substance.substanceClass) throw new Error(`${prefix}: missing substanceClass`);
    if (!substance.aliases?.length) throw new Error(`${prefix}: must have at least one alias`);

    for (const alias of substance.aliases) {
      const normalized = alias.trim().toLowerCase();
      const owner = seenAliases.get(normalized);
      // A shared alias would make fuzzy search resolve arbitrarily between them.
      if (owner && owner !== substance.id) {
        throw new Error(`${prefix}: alias "${alias}" is already claimed by "${owner}"`);
      }
      seenAliases.set(normalized, substance.id);
    }

    const { pk } = substance;
    if (pk.route === 'oral' || pk.route === 'zero-order-elimination') {
      if (!(pk.ka > 0)) throw new Error(`${prefix}: ka must be positive`);
      if (!(pk.vd > 0)) throw new Error(`${prefix}: vd must be positive`);
      if (!(pk.bioavailability > 0 && pk.bioavailability <= 1)) {
        throw new Error(`${prefix}: bioavailability must be in (0, 1]`);
      }
      if (!(pk.molecularWeightGMol > 0)) throw new Error(`${prefix}: molecularWeightGMol must be positive`);
      if (!(pk.brainPlasmaRatio > 0)) throw new Error(`${prefix}: brainPlasmaRatio must be positive`);
      if (pk.route === 'oral' && !(pk.ke > 0)) throw new Error(`${prefix}: ke must be positive`);
      if (pk.route === 'zero-order-elimination' && !(pk.eliminationRateMgPerLPerHour > 0)) {
        throw new Error(`${prefix}: eliminationRateMgPerLPerHour must be positive`);
      }
    } else if (pk.route === 'direct-release') {
      if (!(pk.ke > 0)) throw new Error(`${prefix}: ke must be positive`);
    } else {
      throw new Error(`${prefix}: unknown pk.route`);
    }

    const { doseRange } = substance;
    if (!doseRange) throw new Error(`${prefix}: missing doseRange`);
    if (!(doseRange.min < doseRange.max)) throw new Error(`${prefix}: doseRange.min must be < max`);
    if (!(doseRange.default >= doseRange.min && doseRange.default <= doseRange.max)) {
      throw new Error(`${prefix}: doseRange.default must be within [min, max]`);
    }
    if (!(doseRange.step > 0)) throw new Error(`${prefix}: doseRange.step must be positive`);
    if (!doseRange.unit) throw new Error(`${prefix}: doseRange.unit must be non-empty`);
    if (!(doseRange.displayScale > 0)) throw new Error(`${prefix}: doseRange.displayScale must be positive`);

    if (!substance.bindings?.length) {
      throw new Error(`${prefix}: must have at least one receptor binding`);
    }
    for (const binding of substance.bindings) {
      if (!RECEPTOR_IDS.includes(binding.receptor as (typeof RECEPTOR_IDS)[number])) {
        throw new Error(`${prefix}: unknown receptor id "${binding.receptor}"`);
      }
      if (!(binding.kiNM > 0)) {
        throw new Error(`${prefix}: kiNM for "${binding.receptor}" must be positive`);
      }
      if (!binding.citation?.trim()) {
        throw new Error(`${prefix}: binding for "${binding.receptor}" is missing a citation`);
      }
      const regions = RECEPTOR_TO_REGIONS[binding.receptor as (typeof RECEPTOR_IDS)[number]];
      if (!regions?.length) {
        throw new Error(`${prefix}: receptor "${binding.receptor}" has no region mapping`);
      }
      for (const regionId of regions) {
        if (!REGION_IDS.includes(regionId)) {
          throw new Error(`${prefix}: receptor "${binding.receptor}" maps to unknown region "${regionId}"`);
        }
      }
    }

    if (!substance.colorPalette?.primary || !substance.colorPalette?.secondary) {
      throw new Error(`${prefix}: colorPalette must define primary and secondary colors`);
    }
    if (!(substance.colorPalette.particleSpeedMultiplier > 0)) {
      throw new Error(`${prefix}: colorPalette.particleSpeedMultiplier must be positive`);
    }
  }
}
